import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { FormComponentWithDialog } from "./FormComponent";
import { DraggableComponent } from "./DraggableComponent";
import {
  LayoutGrid,
  Palette,
  Type,
  Eye,
  Save,
  Loader2,
  Radio,
  ArrowLeft,
  HeadingIcon,
  FormInput,
  Hash,
  Phone,
  AtSign,
  MessageSquare,
  CircleDot,
  CheckSquare,
  Upload,
  SeparatorHorizontal,
  Copy,
  Mail,
  FileUp,
  Minus,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  ChevronDownSquare,
  Calendar,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { FieldOptionsDrawer } from "./FieldOptionsDrawer";
import { FormPreviewModal } from "./FormPreviewModal";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateForm, useUpdateForm } from "@/hooks/mutations/forms";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import "react-toastify/dist/ReactToastify.css";
import PageHeader from "../Common/PageHeader";

const formComponents = [
  {
    id: "heading",
    type: "heading",
    label: "Heading",
    sublabel: "",
    placeholder: "Enter heading text ",
    icon: HeadingIcon,
  },
  {
    id: "text",
    type: "text",
    label: "Text Input",
    sublabel: "",
    placeholder: "Enter text ",
    max_length: "",
    icon: FormInput,
  },
  {
    id: "number",
    type: "number",
    label: "Number Input",
    sublabel: "",
    placeholder: "Enter number ",
    icon: Hash,
  },
  {
    id: "phone",
    type: "phone",
    label: "Phone Number",
    sublabel: "",
    placeholder: "Enter phone number ",
    icon: Phone,
  },
  {
    id: "email",
    type: "email",
    label: "Email Input",
    sublabel: "",
    placeholder: "Enter email ",
    icon: Mail,
  },
  {
    id: "textarea",
    type: "textarea",
    label: "Text Area",
    sublabel: "",
    placeholder: "Enter long text ",
    max_length: "",
    icon: MessageSquare,
  },
  {
    id: "select",
    type: "select",
    label: "Dropdown",
    sublabel: "",
    placeholder: "Enter an option ",
    icon: ChevronDownSquare,
  },
  {
    id: "radio",
    type: "radio",
    label: "Radio Button",
    sublabel: "",
    placeholder: "Enter radio button ",
    icon: CircleDot,
  },
  {
    id: "checkbox",
    type: "checkbox",
    label: "Checkbox",
    sublabel: "",
    placeholder: "Enter checkbox ",
    icon: CheckSquare,
  },
  {
    id: "image",
    type: "file",
    label: "Upload File",
    sublabel: "",
    placeholder: "Enter upload or drag an file or image ",
    icon: FileUp,
  },
  {
    id: "spacer",
    type: "spacer",
    label: "Spacer Line",
    sublabel: "",
    height: 2,
    icon: Minus,
  },
  {
    id: "date",
    type: "date",
    label: "Date Picker",
    sublabel: "",
    placeholder: "Select date ",
    icon: Calendar,
  },
  {
    id: "university_repeatable",
    type: "university_repeatable",
    label: "University Repeatable",
    sublabel: "",
    placeholder: "Add university details",
    icon: Copy,
  },
  // {
  //   id: "wysiwyg",
  //   type: "wysiwyg",
  //   label: "Rich Text Editor",
  //   sublabel: "",
  //   placeholder: "Enter rich text content ",
  //   icon: Copy
  // },
];

export function FormBuilder() {
  const location = useLocation();
  const navigate = useNavigate();
  const form = location.state?.form; // Access the form data
  const [formFields, setFormFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [savedForms, setSavedForms] = useState({
    personal: [],
    jobSpecific: [],
  });
  const [showSavedForms, setShowSavedForms] = useState(false);
  const [formType, setFormType] = useState(form?.type ?? "Personal");
  const [formName, setFormName] = useState(form ? form.name : ""); // Initialize form name
  const [isSaving, setIsSaving] = useState(false);
  const createFormMutation = useCreateForm();
  const updateFormMutation = useUpdateForm();
  const drawerRef = useRef(null);
  const { showToast } = useToastStore;
  useEffect(() => {
    console.log("FormBuilder");
    if (form) {
      console.log("Form", form);
      // Map API fields to internal structure if using API data
      const convertedFields = (form.fields || [])
        .map((field) => {
          const baseField = {
            // preserve existing properties if already in new structure
            ...field,
            id: field.id || uuidv4(),
            type: field.field_type || field.type,
            is_required: field.is_required == "1",
          };

          // Special handling for university_repeatable fields when loading for editing
          if ((field.field_type || field.type) == "university_repeatable") {
            try {
              if (field.options) {
                // Parse the JSON options to extract labels and dropdown options
                const structuredOptions = JSON.parse(field.options);
                console.log("Loading university field:", structuredOptions);

                const fieldLabels = {};
                let universityOptions = [];
                let degreeOptions = [];
                let fieldOfStudyOptions = [];

                // Extract data from structured options
                structuredOptions.forEach((subField, index) => {
                  console.log(`SubField ${index}:`, subField);

                  if (subField.field_type === "select") {
                    const labelLower = subField.label.toLowerCase();

                    // More flexible matching for university field (check for first occurrence or specific patterns)
                    if (
                      index === 0 ||
                      labelLower.includes("university") ||
                      labelLower.includes("name") ||
                      labelLower.includes("institution")
                    ) {
                      if (!fieldLabels.universityLabel) {
                        // Only set if not already set
                        fieldLabels.universityLabel = subField.label;
                        universityOptions = subField.options || [];
                        console.log(
                          "Found university field:",
                          subField.label,
                          universityOptions,
                        );
                      }
                    } else if (
                      labelLower.includes("degree") ||
                      labelLower.includes("qualification") ||
                      labelLower.includes("level")
                    ) {
                      fieldLabels.degreeLabel = subField.label;
                      degreeOptions = subField.options || [];
                      console.log(
                        "Found degree field:",
                        subField.label,
                        degreeOptions,
                      );
                    } else if (
                      labelLower.includes("study") ||
                      labelLower.includes("subject") ||
                      labelLower.includes("field") ||
                      labelLower.includes("major")
                    ) {
                      fieldLabels.fieldOfStudyLabel = subField.label;
                      fieldOfStudyOptions = subField.options || [];
                      console.log(
                        "Found field of study:",
                        subField.label,
                        fieldOfStudyOptions,
                      );
                    }
                  } else if (subField.field_type === "number") {
                    const labelLower = subField.label.toLowerCase();
                    if (
                      labelLower.includes("graduation") ||
                      labelLower.includes("year")
                    ) {
                      fieldLabels.graduationYearLabel = subField.label;
                      console.log("Found graduation year:", subField.label);
                    } else if (
                      labelLower.includes("gpa") ||
                      labelLower.includes("grade")
                    ) {
                      fieldLabels.gpaLabel = subField.label;
                      console.log("Found GPA field:", subField.label);
                    }
                  } else if (subField.field_type === "text") {
                    const labelLower = subField.label.toLowerCase();
                    if (
                      labelLower.includes("university") ||
                      labelLower.includes("name") ||
                      labelLower.includes("institution")
                    ) {
                      fieldLabels.universityLabel = subField.label;
                      console.log(
                        "Found university text field:",
                        subField.label,
                      );
                    } else if (
                      labelLower.includes("degree") ||
                      labelLower.includes("qualification") ||
                      labelLower.includes("level")
                    ) {
                      fieldLabels.degreeLabel = subField.label;
                      console.log("Found degree text field:", subField.label);
                    } else if (
                      labelLower.includes("study") ||
                      labelLower.includes("subject") ||
                      labelLower.includes("field") ||
                      labelLower.includes("major")
                    ) {
                      fieldLabels.fieldOfStudyLabel = subField.label;
                      console.log(
                        "Found field of study text field:",
                        subField.label,
                      );
                    }
                  }
                });

                // Set up the field with extracted data
                baseField.fieldLabels = fieldLabels;
                baseField.universityOptions = universityOptions;
                baseField.degreeOptions = degreeOptions;
                baseField.fieldOfStudyOptions = fieldOfStudyOptions;
                baseField.options = []; // Set to empty array for form builder

                console.log("Parsed university field for editing:", baseField);
              }
            } catch (error) {
              console.error("Error parsing university options:", error);
              baseField.options = [];
            }
          } else {
            // Handle regular fields (non-university)
            baseField.options = field.options
              ? field.options.split(",").map((opt) => opt.trim())
              : field.options;
          }

          return baseField;
        })
        .sort((a, b) => parseInt(a.field_order) - parseInt(b.field_order));

      setFormFields(convertedFields);
      setFormName(form.name || form.title);
      // setFormType(form.type || "Personal");
      //set form type to personal or job specific
      setFormType(form.type === "0" ? "Personal" : "Job Specific");
    } else {
      setFormFields([]);
    }
  }, [form]);

  // Load saved forms on mount
  useEffect(() => {
    const forms = JSON.parse(localStorage.getItem("savedForms") || "[]");
    setSavedForms(forms);
  }, []);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
        setSelectedField(null);
      }
    };

    if (isDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDrawerOpen]);

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    // If dropping in the same place
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // If reordering within form preview
    if (
      source.droppableId === "form-preview" &&
      destination.droppableId === "form-preview"
    ) {
      const newFields = Array.from(formFields);
      const [movedField] = newFields.splice(source.index, 1);

      newFields.splice(destination.index, 0, movedField);
      setFormFields(newFields);
      return;
    }

    // If dragging from components to form preview
    if (
      source.droppableId === "components" &&
      destination.droppableId === "form-preview"
    ) {
      const componentType = draggableId;
      const component = formComponents.find((c) => c.type === componentType);

      if (component) {
        const newField = { ...component, id: uuidv4() };
        const newFields = Array.from(formFields);
        newFields.splice(destination.index, 0, newField);
        setFormFields(newFields);
      }
    }
  };

  // Validation function to ensure required fields are provided.
  const validateFormBuilderForm = () => {
    if (!formName.trim()) {
      showToast({
        message: "Form name is required.",
        type: "error",
      });
      return false;
    }
    if (!formType.trim()) {
      showToast({
        message: "Form type is required.",
        type: "error",
      });
      return false;
    }
    for (let i = 0; i < formFields.length; i++) {
      const field = formFields[i];
      if (!field.label || !field.label.trim()) {
        showToast({
          message: `Field label is required for field at position ${i + 1}.`,
          type: "error",
        });
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    // Apply frontend validation on formName, formType, and field labels
    if (!validateFormBuilderForm()) {
      return;
    }

    setIsSaving(true);

    const payload = {
      title: formName,
      type: formType === "Personal" ? "0" : "1",
      fields: formFields.map((field, index) => {
        // Debugging: Log the options to understand their structure
        // console.log(`Field ${index + 1} options:`, field.options);

        const baseField = {
          label: field.label,
          sublabel: field.sublabel || null,
          field_type: field.type,
          placeholder: field.placeholder || null,
          options: Array.isArray(field.options)
            ? field.options
                .map((opt) => {
                  // Extract the label property from each option object
                  if (typeof opt === "object" && opt !== null) {
                    console.log(`Option object:`, opt);
                    return opt.label || ""; // Use the label property
                  }
                  return opt;
                })
                .join(",")
            : field.options,
          is_required: field.is_required ? "1" : "0",
          max_length: field?.max_length ?? null,
          field_order: String(index + 1),
          // is_csv: field.isCSVUploaded ? true : false,
          is_csv: field.is_csv || field.isCSVUploaded || false,

          has_condition: field.has_condition || false,
          conditions: field.conditions
            ? field.conditions.map((condition) => ({
                label: condition.label || "",
                placeholder: condition.placeholder || "",
                trigger_value: condition.trigger_value || "",
              }))
            : [],
        };

        // Add university-specific fields if this is a university_repeatable field
        if (field.type === "university_repeatable") {
          // Create structured options array instead of separate variables
          const structuredOptions = [];

          // Add university field
          if (
            field.fieldLabels?.universityLabel ||
            (field.universityOptions && field.universityOptions.length > 0)
          ) {
            structuredOptions.push({
              label: field.fieldLabels?.universityLabel || "University Name",
              sublabel: "",
              options: field.universityOptions || [],
              field_type: "select",
              placeholder: "",
              is_required: true,
            });
          }

          // Add degree field
          if (
            field.fieldLabels?.degreeLabel ||
            (field.degreeOptions && field.degreeOptions.length > 0)
          ) {
            structuredOptions.push({
              label: field.fieldLabels?.degreeLabel || "Qualification",
              sublabel: "",
              options: field.degreeOptions || [],
              field_type: "select",
              placeholder: "",
              is_required: true,
            });
          }

          // Add field of study
          if (
            field.fieldLabels?.fieldOfStudyLabel ||
            (field.fieldOfStudyOptions && field.fieldOfStudyOptions.length > 0)
          ) {
            structuredOptions.push({
              label: field.fieldLabels?.fieldOfStudyLabel || "Subject",
              sublabel: "",
              options: field.fieldOfStudyOptions || [],
              field_type: "select",
              placeholder: "",
              is_required: true,
            });
          }

          // Add graduation year
          if (field.fieldLabels?.graduationYearLabel) {
            structuredOptions.push({
              label:
                field.fieldLabels?.graduationYearLabel || "Graduation Year",
              sublabel: "",
              options: [],
              field_type: "number",
              placeholder: "e.g., 2023",
              is_required: true,
            });
          }

          // Add GPA
          if (field.fieldLabels?.gpaLabel) {
            structuredOptions.push({
              label: field.fieldLabels?.gpaLabel || "GPA",
              sublabel: "",
              options: [],
              field_type: "number",
              placeholder: "e.g., 3.8",
              is_required: false,
            });
          }

          // Override options with structured JSON (not separate variables)
          if (structuredOptions.length > 0) {
            baseField.options = JSON.stringify(structuredOptions);
            console.log(
              "FormBuilder - University field structured options:",
              baseField.options,
            );
          } else {
            baseField.options = null;
          }

          // DON'T send separate field_labels, university_options, degree_options variables
        }

        return baseField;
      }),
    };

    try {
      if (form?.id) {
        // Updating existing form
        await updateFormMutation.mutateAsync({
          formId: form.id,
          formData: payload,
        });
        navigate("/forms");
        showToast({
          message: "Form updated successfully.",
          type: "success",
        });
      } else {
        // Creating new form
        await createFormMutation.mutateAsync(payload);
        navigate("/forms");
        showToast({
          message: "Form created successfully.",
          type: "success",
        });
      }
      // setTimeout(() => {
      //   navigate("/forms");
      // }, 2000); // Delay navigation by 2 seconds to show the toast message
    } catch (error) {
      showToast({
        message: "Form not submitted.",
        type: "error",
      });
      console.error("Error submitting form:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddField = (field) => {
    setFormFields((prev) => [...prev, field]);
  };

  const handleDeleteField = (fieldId) => {
    setFormFields((prev) => prev.filter((field) => field.id !== fieldId));
    // If the field being deleted is currently selected, close the drawer.
    if (selectedField && selectedField.id === fieldId) {
      setIsDrawerOpen(false);
      setSelectedField(null);
    }
  };

  const handleMoveField = (fieldId, direction) => {
    const newFields = Array.from(formFields);
    const index = newFields.findIndex((field) => field.id === fieldId);
    if (index !== -1) {
      if (direction === "up") {
        if (index > 0) {
          [newFields[index - 1], newFields[index]] = [
            newFields[index],
            newFields[index - 1],
          ];
        }
      } else if (direction === "down") {
        if (index < newFields.length - 1) {
          [newFields[index], newFields[index + 1]] = [
            newFields[index + 1],
            newFields[index],
          ];
        }
      }
      setFormFields(newFields);
    }
  };

  const handleFieldSelect = (fieldId) => {
    const field = formFields.find((f) => f.id === fieldId);
    setSelectedField(field);
    setIsDrawerOpen(true);
  };

  const handleFieldUpdate = (updates) => {
    if (!selectedField) return;
    console.log("updates", updates);
    setFormFields((prev) =>
      prev.map((field) =>
        field.id === selectedField.id ? { ...field, ...updates } : field,
      ),
    );
    setSelectedField((prev) => ({ ...prev, ...updates }));
  };

  const handleDeleteForm = (formId) => {
    if (window.confirm("Are you sure you want to delete this form?")) {
      const updatedForms = savedForms.filter((form) => form.id !== formId);
      localStorage.setItem("savedForms", JSON.stringify(updatedForms));
      setSavedForms(updatedForms);
    }
  };

  const handleEditForm = (form) => {
    setFormFields(form.fields);
    setShowSavedForms(false);
  };

  const handleFormTypeChange = (e) => {
    setFormType(e.target.value);
  };

  const handlePreview = () => {
    setIsPreviewOpen(true);
  };

  const handleDuplicateField = (fieldToDuplicate) => {
    // Create a deep copy of the field
    const duplicatedField = JSON.parse(JSON.stringify(fieldToDuplicate));

    // Generate a new unique ID for the duplicated field
    duplicatedField.id = `field-${Date.now()}`;

    // Find the index of the original field
    const originalIndex = formFields.findIndex(
      (field) => field.id === fieldToDuplicate.id,
    );

    // Insert the duplicated field right after the original
    const newFields = [
      ...formFields.slice(0, originalIndex + 1),
      duplicatedField,
      ...formFields.slice(originalIndex + 1),
    ];

    // Update the form state
    setFormFields(newFields);
  };

  return (
    <div className="p-6">
      <div className="w-full  max-w-[1600px]">
        <div className="lg:pr-[20rem] md:pr-[20rem]">
          <PageHeader
            heading={form ? `Edit Form: ${form.title}` : "Create New Form"}
            headingIcon="LayoutGrid"
            navigationText={"Back to Forms"}
            navigateTo={"/forms"}
            customElements={() => (
              <button
                onClick={() => navigate("/forms")}
                className="px-4 py-2 bg-primary-dark  text-white rounded-md hover:bg-primary-dark/90 transition-colors"
              >
                View Saved Forms
              </button>
            )}
          />

          {/* Form Name and Form Type in one line */}
          <div className="flex space-x-4 mb-4">
            <div className="w-1/2">
              <label
                htmlFor="formName"
                className="block text-foreground font-medium mb-2"
              >
                Form Name
              </label>
              <input
                type="text"
                id="formName"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="bg-card border border-brand rounded-lg p-2 w-full "
                placeholder="Enter form name"
                required
              />
            </div>
            <div className="w-1/2">
              <label
                htmlFor="formType"
                className="block text-foreground font-medium mb-2"
              >
                Form Type
              </label>
              <select
                id="formType"
                value={formType}
                onChange={handleFormTypeChange}
                className="bg-card border border-brand rounded-lg p-2 w-full"
              >
                <option value="">Select Type</option>
                <option value="Personal" selected={formType === "Personal"}>
                  Personal
                </option>
                <option
                  value="Job Specific"
                  selected={formType === "Job Specific"}
                >
                  Job Specific
                </option>
              </select>
            </div>
          </div>
        </div>

        {showSavedForms ? (
          <SavedForms
            forms={savedForms}
            onEdit={handleEditForm}
            onDelete={handleDeleteForm}
            onPreview={(form) => {
              setFormFields(form.fields);
              setIsPreviewOpen(true);
            }}
          />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="grid grid-cols-9  gap-6 w-f mx-auto">
              <div className="col-span-3">
                <div className="bg-muted p-6 rounded-lg shadow-sm border border-brand/20">
                  <div className="flex items-center gap-2 mb-4">
                    <Type className="w-5 h-5 text-foreground" />
                    <h2 className="text-lg font-semibold text-foreground">
                      Form Fields
                    </h2>
                  </div>
                  <Droppable droppableId="components" isDropDisabled={true}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="space-y-3"
                      >
                        {formComponents.map((component, index) => (
                          <DraggableComponent
                            key={component.type}
                            field={component}
                            index={index}
                            onAdd={handleAddField}
                          />
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              </div>

              {/* Middle Column - Form Preview */}
              <div className="col-span-6">
                <div className="sticky top-0 bg-card border-b z-10">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Palette className="w-5 h-5 text-foreground" />
                      <h2 className="text-lg font-semibold text-foreground">
                        Form Preview
                      </h2>
                    </div>

                    <div className="flex justify-end">
                      <div className="flex space-x-4">
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={isSaving}
                          className="flex items-center gap-2 px-4 py-2 bg-primary-dark text-white rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-5 h-5" />
                              Save Form
                            </>
                          )}
                        </button>

                        {/* <button
                          type="button"
                          onClick={handlePreview}
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
                        >
                          <Eye className="w-5 h-5" />
                          Preview
                        </button> */}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-muted p-6 rounded-lg shadow-sm border border-brand/20 min-h-[calc(100vh-12rem)]">
                  <Droppable droppableId="form-preview">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-3 min-h-[500px] rounded-lg transition-colors ${
                          snapshot.isDraggingOver ? "bg-primary/10" : ""
                        }`}
                      >
                        {formFields.map((field, index) => (
                          <FormComponentWithDialog
                            key={field.id}
                            field={field}
                            index={index}
                            onDelete={handleDeleteField}
                            onMove={handleMoveField}
                            onSelect={handleFieldSelect}
                            onDuplicate={handleDuplicateField}
                            isSelected={selectedField?.id === field.id}
                            isFirst={index === 0}
                            isLast={index === formFields.length - 1}
                          />
                        ))}
                        {provided.placeholder}
                        {formFields.length === 0 &&
                          !snapshot.isDraggingOver && (
                            // <div className="flex items-center justify-center h-96 border-2 border-dashed border-brand/30 rounded-lg">
                            //   <p className="text-foreground">
                            //     Drag and drop components here to build your form
                            <div className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-brand/30 rounded-lg bg-card/50">
                              <p className="text-foreground text-lg font-medium mb-2">
                                Drag and drop form fields from the left panel
                              </p>
                              <p className="text-foreground/80 text-sm">
                                Start building your form
                              </p>
                            </div>
                          )}
                      </div>
                    )}
                  </Droppable>
                </div>
              </div>

              {/* Right Column - Empty Space */}
              <div className="col-span-3">
                {/* This space intentionally left empty */}
              </div>
            </div>
          </DragDropContext>
        )}

        {/* Field Options Drawer */}
        <div ref={drawerRef}>
          <FieldOptionsDrawer
            field={selectedField}
            onUpdate={handleFieldUpdate}
            onClose={() => {
              setIsDrawerOpen(false);
              setSelectedField(null);
            }}
            isOpen={isDrawerOpen}
          />
        </div>

        {/* Preview Modal */}
        <FormPreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          formFields={formFields}
          onSave={handleSave}
          form={{
            id: form?.id,
            name: formName,
            type: formType,
            createdAt: form?.createdAt,
          }}
        />
      </div>
    </div>
  );
}
